import { ChangeEmitter, type Store } from "@/data/repositories";
import type { Clock } from "@/domain/clock";
import { todayKey } from "@/domain/dates";
import type { Entry, Subscription, UserSettings } from "@/domain/entities";
import { sha256Hex } from "@/domain/sha256";
import type { AccountService } from "./AccountService";
import { ApiError, type Api, type ArchiveMeta } from "./ApiClient";

export interface ArchiveSnapshot {
  formatVersion: 1;
  exportedAt: string;
  subscriptions: Subscription[];
  entries: Entry[];
  settings: Pick<UserSettings, "locale" | "theme" | "activeSubscriptionId">;
}

export type ArchiveState =
  | { kind: "idle" }
  | { kind: "scheduled" }
  | { kind: "uploading" }
  | { kind: "done"; at: string }
  | { kind: "failed"; at: string; code: string };

/** gzip in and out; the browser uses CompressionStream, tests plug in zlib. */
export interface Compressor {
  gzip(bytes: Uint8Array): Promise<Uint8Array>;
  gunzip(bytes: Uint8Array): Promise<Uint8Array>;
}

export class StreamCompressor implements Compressor {
  gzip(bytes: Uint8Array): Promise<Uint8Array> {
    return this.pipe(bytes, new CompressionStream("gzip"));
  }

  gunzip(bytes: Uint8Array): Promise<Uint8Array> {
    return this.pipe(bytes, new DecompressionStream("gzip"));
  }

  private async pipe(bytes: Uint8Array, transform: CompressionStream | DecompressionStream): Promise<Uint8Array> {
    // lib.dom types the writable side as BufferSource, which pipeThrough's generic does not accept as is.
    const stream = new Blob([new Uint8Array(bytes)]).stream().pipeThrough(transform as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

interface Deps {
  api: Api;
  store: Store;
  account: AccountService;
  clock: Clock;
  compressor: Compressor;
  timers?: Timers;
  changeSources: readonly ChangeEmitter[];
}

export const ARCHIVE_DEBOUNCE_MS = 30_000;
export const ARCHIVE_RETRY_MS = [60_000, 5 * 60_000, 15 * 60_000] as const;

/**
 * Keeps the newest state of the diary on the server for a signed-in user and
 * brings an older state back on request. Uploads are quiet and debounced;
 * nothing is ever restored without an explicit call.
 */
export class ArchiveService {
  readonly changes = new ChangeEmitter();
  state: ArchiveState = { kind: "idle" };
  private timer: unknown = null;
  private retries = 0;
  private running: Promise<void> | null = null;
  private readonly timers: Timers;

  constructor(private readonly deps: Deps) {
    this.timers = deps.timers ?? { setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };
  }

  /** Subscribes to data changes and uploads once now; call once at app start. */
  start(): () => void {
    const unsubscribes = this.deps.changeSources.map((source) => source.subscribe(() => this.schedule()));
    void this.run();
    return () => {
      for (const u of unsubscribes) u();
      this.cancelTimer();
    };
  }

  /** Waits for the diary to settle, then uploads. */
  schedule(delayMs = ARCHIVE_DEBOUNCE_MS): void {
    this.cancelTimer();
    this.setState({ kind: "scheduled" });
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delayMs);
  }

  /** Uploads now when signed in and the diary differs from the last archive. */
  run(): Promise<void> {
    if (!this.running) this.running = this.upload().finally(() => { this.running = null; });
    return this.running;
  }

  async snapshot(): Promise<ArchiveSnapshot> {
    const [subscriptions, entries, settings] = await Promise.all([
      this.deps.store.subscriptions.exportAll(),
      this.deps.store.entries.exportAll(),
      this.deps.store.settings.get(),
    ]);
    const kept: ArchiveSnapshot["settings"] = { theme: settings.theme };
    if (settings.locale) kept.locale = settings.locale;
    if (settings.activeSubscriptionId) kept.activeSubscriptionId = settings.activeSubscriptionId;
    return { formatVersion: 1, exportedAt: this.deps.clock.now().toISOString(), subscriptions, entries, settings: kept };
  }

  /** Content hash over everything but the export time, so an unchanged diary hashes the same. */
  static hash(snapshot: ArchiveSnapshot): string {
    const content: Partial<ArchiveSnapshot> = { ...snapshot };
    delete content.exportedAt;
    return sha256Hex(canonical(content));
  }

  list(): Promise<ArchiveMeta[]> {
    return this.deps.account.withToken((token) => this.deps.api.listArchives(token));
  }

  /** Archives the current state first, then replaces the diary with the chosen archive. */
  async restore(id: string): Promise<void> {
    this.cancelTimer();
    await this.deps.account.withToken(async (token) => {
      const current = await this.snapshot();
      const currentBytes = await this.deps.compressor.gzip(new TextEncoder().encode(JSON.stringify(current)));
      await this.deps.api.putArchive(token, todayKey(this.deps.clock), "manual", currentBytes, ArchiveService.hash(current));
      const bytes = await this.deps.api.getArchive(token, id);
      await applySnapshot(this.deps.store, await this.deps.compressor.gunzip(bytes));
    });
    this.schedule();
  }

  private async upload(): Promise<void> {
    this.cancelTimer();
    const settings = await this.deps.store.settings.get();
    if (!settings.session) {
      this.setState({ kind: "idle" });
      return;
    }
    const snapshot = await this.snapshot();
    const hash = ArchiveService.hash(snapshot);
    if (settings.lastArchive?.hash === hash) {
      this.setState({ kind: "done", at: settings.lastArchive.at });
      return;
    }
    this.setState({ kind: "uploading" });
    try {
      const day = todayKey(this.deps.clock);
      const body = await this.deps.compressor.gzip(new TextEncoder().encode(JSON.stringify(snapshot)));
      const result = await this.deps.account.withToken((token) => this.deps.api.putArchive(token, day, "auto", body, hash));
      await this.deps.store.settings.update({ lastArchive: { hash, at: result.createdAt, day } });
      this.retries = 0;
      this.setState({ kind: "done", at: result.createdAt });
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "network";
      this.setState({ kind: "failed", at: this.deps.clock.now().toISOString(), code });
      if (error instanceof ApiError && error.status === 401) return; // the session is gone; nothing to retry
      const delay = ARCHIVE_RETRY_MS[Math.min(this.retries, ARCHIVE_RETRY_MS.length - 1)]!;
      this.retries += 1;
      this.timer = this.timers.setTimeout(() => {
        this.timer = null;
        void this.run();
      }, delay);
    }
  }

  private setState(state: ArchiveState): void {
    this.state = state;
    this.changes.emit();
  }

  private cancelTimer(): void {
    if (this.timer !== null) {
      this.timers.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

/** Replaces the diary with a decompressed snapshot; the device's own session and archive bookkeeping stay. */
export async function applySnapshot(store: Store, json: Uint8Array): Promise<void> {
  const snapshot = JSON.parse(new TextDecoder().decode(json)) as Partial<ArchiveSnapshot>;
  if (snapshot.formatVersion !== 1 || !Array.isArray(snapshot.subscriptions) || !Array.isArray(snapshot.entries)) throw new Error("Unsupported archive format");
  await store.subscriptions.importAll(snapshot.subscriptions);
  await store.entries.importAll(snapshot.entries);
  const settings = snapshot.settings ?? { theme: "system" };
  await store.settings.update({ theme: settings.theme, locale: settings.locale, activeSubscriptionId: settings.activeSubscriptionId });
}

/** JSON with object keys sorted at every level; arrays keep their order. */
export function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === "object" && !Array.isArray(v) ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : v,
  );
}
